import ModalBody from 'react-bootstrap/ModalBody'
import ModalFooter from 'react-bootstrap/ModalFooter'
import { useCallback, useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { DivisionDeNegocio, SitiosDivNegocio, style, Usuario } from '../models/usuario.type'
import {
  useGuardarUsuarioMutation,
  useActualizarUsuarioMutation,
  useRecuperarDivisionesNegocioQuery,
} from '../../../redux/api'
import Swal from 'sweetalert2'
import { CargaInfo } from '../../../components/layout/CargaInfo'
import { Cargando } from '../../../components/layout/Cargando'
import { alertUsuarios } from '../../../utils/alerts/AlertUsuarios'

type Props = {
  usuarioSitios: string[]
  isModoEditar: boolean
  formulario: Usuario | undefined
  cerrarModal: () => void
  regresar: (mostrarSelecSitios: boolean) => void
  refetch: () => void
  filtroDivisionesNegocio: string
  refetchConsulta: () => void
}

export const normalizarUsuarioGuardar = (usuario: Partial<Usuario>) => ({
  ...usuario,
  telefono: usuario.telefono?.trim() || null,
})

/**
 * Segundo paso del modal de usuario: seleccion de sitios por division de negocio.
 * - Muestra las divisiones con sus sitios (checkbox).
 * - En modo editar, preselecciona los sitios ya asignados.
 * - Al confirmar, guarda/actualiza el usuario via API con los sitios seleccionados.
 */
export const OpcionesSitios = ({
  usuarioSitios,
  isModoEditar,
  formulario,
  filtroDivisionesNegocio,
  cerrarModal,
  regresar,
  refetch,
  refetchConsulta,
}: Props) => {
  const [sitios, setSitios] = useState<DivisionDeNegocio[]>([])
  const { data, isSuccess, isError, error, isLoading } = useRecuperarDivisionesNegocioQuery(
    filtroDivisionesNegocio,
    { skip: !filtroDivisionesNegocio }
  )
  const [arrSitios, setArrSitios] = useState<string[]>(usuarioSitios ? usuarioSitios : [])
  //hook para guardar un usuario
  const [
    guardarUsuario,
    {
      isSuccess: isSuccessGuardar,
      isError: isErrorGuardar,
      error: errorGuardar,
      isLoading: isLoadingGuardar,
    },
  ] = useGuardarUsuarioMutation()
  //hook para actualizar un usuario
  const [
    actualizarUsuario,
    {
      isError: isErrorActualizar,
      error: errorActualizar,
      isLoading: isLoadingActualizar,
    },
  ] = useActualizarUsuarioMutation()

  /**
   * @description Agrega un sitio a un arreglo de id's de sitios marcado
   * @author andres.martinez, Hábil MX
   * @param idSitio Id del sitio seleccionado
   */
  const marcarSitio = (idSitio: string) => {
    const nuevoArr = [...arrSitios, idSitio]
    setArrSitios(nuevoArr)
  }

  /**
   * @description Extrae un sitio del arreglo de id' de sitios marcado
   * @author andres.martinez, Hábil MX
   * @param idSitio Id del sitio seleccionado
   */
  const desmarcarSitio = (idSitio: string) => {
    const nuevoArr = [...arrSitios]
    setArrSitios(nuevoArr.filter((s: string) => s !== idSitio))
  }

  /**
   * @description Guarda o actualiza un usuario, verifica si los sitios seleccionados,
   *              coincide con los mostrados.
   * @author andres.martinez, Hábil MX
   */
  const guardarActualizarUsuario = useCallback(() => {
    const arraySitios: string[] = []
    sitios.forEach((sitio: DivisionDeNegocio) => {
      sitio.sitios.forEach((s: SitiosDivNegocio) => {
        if (arrSitios.includes(s.idSitio)) {
          arraySitios.push(s.idSitio)
        }
      })
    })
    const usuario = normalizarUsuarioGuardar({
      ...formulario,
      sitios: arraySitios,
    })

    if (isModoEditar) {
      actualizarUsuario(usuario)
        .unwrap()
        .then(() => {
          toast.success('¡Usuario actualizado!', { style })
          refetchConsulta()
          refetch()
        })
        .catch(() => undefined)
    } else {
      guardarUsuario(usuario)
        .unwrap()
        .then(() => {
          refetchConsulta()
        })
        .catch(() => undefined)
    }
  }, [actualizarUsuario, arrSitios, formulario, guardarUsuario, isModoEditar, refetch, refetchConsulta, sitios])

  useEffect(() => {
    if (isSuccess) {
      setSitios(
        data
          ?.map((dv: DivisionDeNegocio) => {
            return {
              ...dv,
              sitios: dv.sitios.map((s: SitiosDivNegocio) => {
                return {
                  ...s,
                  esSeleccionada: isModoEditar && usuarioSitios?.includes(s.idSitio),
                }
              }),
            }
          })
          .sort((a, b) => {
            const nameA = a.divisionDeNegocio
            const nameB = b.divisionDeNegocio
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }
            return 0
          })
      )
    }
  }, [data, isModoEditar, isSuccess, usuarioSitios])

  useEffect(() => {
    if (isSuccessGuardar) {
      Swal.fire({
        title: 'Usuario guardado!',
        icon: 'success',
        text: 'El usuario se registrado con exito.',
        confirmButtonText: 'Entendido',
        allowOutsideClick: true,
        allowEscapeKey: true,
      }).then((result) => {
        if (result.isConfirmed) {
          cerrarModal()
        }
      })
    }
  }, [cerrarModal, isSuccessGuardar])

  useEffect(() => {
    if (isErrorGuardar || isErrorActualizar) {
      alertUsuarios(isModoEditar, guardarActualizarUsuario, errorActualizar, errorGuardar)
    }
  }, [
    errorActualizar,
    errorGuardar,
    guardarActualizarUsuario,
    isErrorActualizar,
    isErrorGuardar,
    isModoEditar,
  ])

  return (
    <>
      <Toaster />
      {isLoading || isError ? (
        <CargaInfo
          mensaje='Recuperando sitios'
          isError={isError}
          mensajeError='Ocurrió un error al recuperar los sitios'
          errorApi={error}
        />
      ) : (
        <>
          <ModalBody
            className={`${(isLoadingActualizar || isLoadingGuardar) && 'overlay overlay-block'}`}>
            <div className={`row`}>
              {sitios?.map((dv: DivisionDeNegocio) => (
                <div key={dv.divisionDeNegocio} className='col'>
                  <h2 className='text-center pb-4'>{dv.descDivisionDeNegocio}</h2>
                  {dv.sitios.map((sitio: SitiosDivNegocio) => (
                    <div key={sitio.idSitio}>
                      <label
                        className={`h-80px btn btn-outline btn-outline-dashed btn-active-light-primary d-flex flex-stack text-start p-5 mb-5 ${
                          sitio.esSeleccionada && 'active'
                        } ${
                          isLoadingActualizar || isLoadingGuardar ? 'overlay overlay-block' : ''
                        }`}>
                        <div className='d-flex align-items-center me-2'>
                          <div className='form-check form-check-custom form-check-solid form-check-primary me-6'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={sitio.esSeleccionada}
                              onChange={(e) => {
                                const check = e.target.checked
                                setSitios(
                                  sitios.map((dv) => {
                                    return {
                                      ...dv,
                                      sitios: dv.sitios.map((s) => {
                                        if (s.idSitio === sitio.idSitio) {
                                          return {
                                            ...s,
                                            esSeleccionada: check,
                                          }
                                        }
                                        return s
                                      }),
                                    }
                                  })
                                )
                                check ? marcarSitio(sitio.idSitio) : desmarcarSitio(sitio.idSitio)
                              }}
                            />
                          </div>
                          <div className='flex-grow-1'>
                            <h2 className='d-flex align-items-center fs-3 fw-bold flex-wrap'>
                              {sitio.nombre}
                              <span className={`ms-2 badge badge-sm badge-light`}>{sitio.rfc}</span>
                            </h2>
                            <div className='fw-semibold opacity-75'>{sitio.descRazonSocial}</div>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              ))}
              {(isLoadingGuardar || isLoadingActualizar) && (
                <Cargando mensaje='Guardando usuario' />
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div
              className={`d-flex justify-content-between flex-grow-1 ${
                (isLoadingActualizar || isLoadingGuardar) &&
                'overlay overlay-block overlay-wrapper overlay-layer'
              }`}>
              <div>
                <button
                  disabled={isLoadingActualizar || isLoadingGuardar}
                  className={`btn btn-light-primary p-3`}
                  onClick={() => {
                    setSitios([])
                    regresar(false)
                  }}>
                  <span>
                    <i className='me-2 fa-solid fa-arrow-left'></i>
                  </span>
                  Regresar
                </button>
              </div>
              <div>
                <button
                  disabled={isLoadingActualizar || isLoadingGuardar}
                  type='reset'
                  className='btn text-hover-dark btn-cancelar'
                  onClick={cerrarModal}>
                  Cancelar
                </button>
                <button
                  disabled={isLoadingActualizar || isLoadingGuardar}
                  className={`btn btn-primary p-3`}
                  type='button'
                  onClick={guardarActualizarUsuario}>
                  Confirmar
                </button>
              </div>
            </div>
          </ModalFooter>
        </>
      )}
    </>
  )
}
